import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const LogoContext = createContext();

export function LogoProvider({ children }) {
  const [logoUrl, setLogoUrl] = useState(() => localStorage.getItem('appLogoUrl'));

  // Fetch logo from global settings and ensure base64 is stored locally
  useEffect(() => {
    const fetchGlobalLogo = async () => {
      // Check if user is authenticated before accessing Firestore
      // For public pages, use cached logo from localStorage
      if (!auth.currentUser) {
        // Use cached logo if available, otherwise skip Firestore fetch
        const cachedLogo = localStorage.getItem('appLogoUrl');
        if (cachedLogo) {
          setLogoUrl(cachedLogo);
        }
        return;
      }

      try {
        const settingsRef = doc(db, 'settings', 'global');
        const settingsDoc = await getDoc(settingsRef);
        
        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          const globalLogoUrl = settings?.branding?.logoUrl;
          const globalLogoBase64 = settings?.branding?.logoBase64;
          
          if (globalLogoUrl && globalLogoUrl !== logoUrl) {
            updateLogo(globalLogoUrl);
            
            // If we have base64 data in Firestore, store it locally
            if (globalLogoBase64) {
              localStorage.setItem('appLogoBase64', globalLogoBase64);
              console.log('Logo base64 data loaded from Firestore and cached locally');
            } else {
              // If we don't have base64 stored but we have a URL, convert it
              const storedBase64 = localStorage.getItem('appLogoBase64');
              if (!storedBase64) {
                console.log('Converting existing logo URL to base64 for local storage...');
                try {
                  // Use direct image loading approach for Firebase Storage URLs
                  const img = new Image();
                  
                  // Set crossOrigin for Firebase Storage
                  if (globalLogoUrl.includes('firebasestorage.googleapis.com')) {
                    img.crossOrigin = 'anonymous';
                  }
                  
                  img.onload = () => {
                    try {
                      const canvas = document.createElement('canvas');
                      const ctx = canvas.getContext('2d');
                      canvas.width = img.width;
                      canvas.height = img.height;
                      ctx.drawImage(img, 0, 0);
                      
                      const base64Data = canvas.toDataURL('image/png', 0.95);
                      localStorage.setItem('appLogoBase64', base64Data);
                      console.log('Logo URL converted to base64 and stored locally');
                    } catch (canvasError) {
                      console.warn('Canvas conversion failed:', canvasError);
                    }
                  };
                  
                  img.onerror = (error) => {
                    console.warn('Image loading failed during LogoContext init:', error);
                  };
                  
                  img.src = globalLogoUrl;
                } catch (conversionError) {
                  console.warn('Could not convert existing logo to base64:', conversionError);
                }
              }
            }
          }
        }
      } catch (error) {
        // Only log error if it's not a permission error (expected for unauthenticated users)
        if (error.code !== 'permission-denied') {
          console.error('Error fetching global logo:', error);
        }
        // Use cached logo if available
        const cachedLogo = localStorage.getItem('appLogoUrl');
        if (cachedLogo) {
          setLogoUrl(cachedLogo);
        }
      }
    };

    fetchGlobalLogo();

    // Listen for auth state changes
    const unsubscribe = auth.onAuthStateChanged(() => {
      fetchGlobalLogo();
    });

    return () => unsubscribe();
  }, []);

  const updateLogo = (newLogoUrl) => {
    if (newLogoUrl) {
      localStorage.setItem('appLogoUrl', newLogoUrl);
    } else {
      localStorage.removeItem('appLogoUrl');
    }
    setLogoUrl(newLogoUrl);

    // Dispatch custom event for real-time updates
    window.dispatchEvent(new CustomEvent('appLogoChanged', { 
      detail: { logoUrl: newLogoUrl } 
    }));
  };

  // Listen for storage events from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'appLogoUrl') {
        setLogoUrl(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <LogoContext.Provider value={{ logoUrl, updateLogo }}>
      {children}
    </LogoContext.Provider>
  );
}

export const useLogo = () => {
  const context = useContext(LogoContext);
  if (!context) {
    throw new Error('useLogo must be used within a LogoProvider');
  }
  return context;
}; 